import { fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import { renderDragControl, renderConnectControl } from './controlRendering';
import { sqrDistanceBetweenPoints, constrainToAxis } from '../utils/geometry';
import { type IPoint, type Point, type Transform } from 'fabric/fabric-impl';
import { AttachmentPoint, ConnectorEnd } from '../types';
import type Connector from '../objects/Connector';
import { type Attachable } from '../objects/Connector';
import RouterOrthogonal from '../objects/connectorRouters/RouterOrthogonal';
import type WhiteboardController from '../controllers/WhiteboardController';
import type Canvas from '../objects/Canvas';
import { ConnectorRouting } from '../objects/ConnectorRouting';
import { fetchAttachmentPoints } from '../utils/connectorAttachment';

const ATTACHMENT_SNAP_RADIUS = 15;

// Control handle for a draggable point on a connector.
class ConnectorPointControl extends ControlBase {
  // Index of this point on the connector.
  pointIndex: number;
  // The total number of points on the connector.
  numPoints: number;
  // Which end of the connector this point represents (if any).
  whichEnd: ConnectorEnd | null;
  // During a drag operation, which object this control is over.
  overObject: Attachable | null | undefined;

  // During a drag operation, which attachment point this control is over.
  overAttachmentPoint: AttachmentPoint | null;

  actionName = 'dragPoint';
  minDimension = 0;

  constructor(connector: Connector, index: number, numPoints: number) {
    super({});
    let anchorIndex: number;
    if (index > 0) {
      if (
        connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
      ) {
        anchorIndex = 0;
      } else {
        anchorIndex = index - 1;
      }
    } else {
      anchorIndex = numPoints - 1;
    }
    this.actionHandler = this.anchorWrapper(
      anchorIndex,
      this.anchoredActionHandler
    );
    this.pointIndex = index;
    this.numPoints = numPoints;
    if (index === 0) this.whichEnd = ConnectorEnd.CONNECTOR_START;
    else if (index === numPoints - 1)
      this.whichEnd = ConnectorEnd.CONNECTOR_END;
    else this.whichEnd = null;

    // Object that the dragged end of the connector is over.
    this.overObject = null;
  }

  getMouseDownHandler(
    eventData: MouseEvent,
    fabricObject: Connector,
    control: ConnectorPointControl
  ) {
    return () => {
      const connector = fabricObject;
      connector.controller.actionHideObjectToolbar();
      connector.beingControlled = true;
      control.overObject = null;
      return false;
    };
  }

  getMouseUpHandler(
    eventData: MouseEvent,
    fabricObject: Connector,
    control: ConnectorPointControl
  ) {
    return () => {
      const connector = fabricObject;
      connector.canvas.controller.actionResetStylus();
      connector.canvas.controller.actionShowObjectToolbar();
      // Handles undo for split control as well
      connector.canvas.controller.undoManager.completeUndoGroup();
      control.overObject = null;
      connector.fire('movedPoint');
      connector.beingControlled = false;

      // We may have flipped the orientation of an orthogonal line, so recompute
      // the controls.
      connector.createControls();

      return false;
    };
  }

  // Position the control handle.
  positionHandler(dim: IPoint, finalMatrix: any, fabricObject: Connector) {
    const x =
      fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x;
    const y =
      fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;

    return fabric.util.transformPoint(
      { x, y } as Point,
      fabric.util.multiplyTransformMatrices(
        fabricObject.canvas.viewportTransform!,
        fabricObject.calcTransformMatrix(true)
      )
    );
  }

  // TODO: Can we memoize this during a drag.
  _findShapeTargets(
    canvas: Canvas,
    pointer: IPoint,
    connector: Connector,
    ignoreTargets: boolean
  ) {
    if (ignoreTargets) return null;

    const otherEndObject =
      this.whichEnd == null
        ? null
        : connector.attachments[
            (connector.constructor as typeof Connector).otherEnd(this.whichEnd)
          ]?.object;
    // Translate pointer for viewport.
    const vptPointer = fabric.util.transformPoint(
      pointer as Point,
      canvas.viewportTransform!
    );

    const found = canvas.findFirstObject(
      (object): object is Attachable =>
        'connectControlDefinition' in object && object.containsPoint(vptPointer)
    );

    return found && found !== otherEndObject ? found : null;
  }

  /**
   * Snaps connector to a give attachment point while the user is dragging over
   * a shape. If the user attaches to center, find the closest attachmetn point
   * on the shape to the origin of the connector
   */
  _snapToAttachmentPoint(
    canvas: Canvas,
    connector: Connector,
    position: Point
  ) {
    if (!this.overObject) return;

    const radius =
      (ATTACHMENT_SNAP_RADIUS * ATTACHMENT_SNAP_RADIUS) / canvas.getZoom();
    let closestAttachment = fetchAttachmentPoints(this.overObject, {
      withStandoff: true,
    }).find(
      attachment =>
        sqrDistanceBetweenPoints(attachment.position, position.x, position.y) <
        radius
    );

    // If we found no other closer point, default to the center of the shape
    // if the object allows it.
    if (
      this.whichEnd !== null &&
      closestAttachment &&
      closestAttachment.attachmentPoint === AttachmentPoint.Center
    ) {
      const position = this.overObject.positionForConnectorAttachment(
        AttachmentPoint.Center,
        connector,
        this.whichEnd
      );

      if (position) {
        closestAttachment = {
          position,
          attachmentPoint: AttachmentPoint.Center,
        };
      }
    }

    if (closestAttachment) {
      this.overAttachmentPoint = closestAttachment.attachmentPoint;
      position.x = closestAttachment.position.x;
      position.y = closestAttachment.position.y;
    } else {
      this.overAttachmentPoint = null;
    }
  }

  // Dynamically attach or detach the connector from objects during the
  // drag so that that the connector rendering reflects the attachment.
  _attachDetachDuringDrag(
    connector: Connector,
    control: ConnectorPointControl
  ) {
    if (control.overObject && control.overAttachmentPoint) {
      const whichEnd =
        control.pointIndex === 0
          ? ConnectorEnd.CONNECTOR_START
          : ConnectorEnd.CONNECTOR_END;
      control.overObject.attachConnector(
        connector,
        whichEnd,
        control.overAttachmentPoint
      );
    } else {
      // Detach the connector from any object it was connected to.
      connector.detachObjectAtIndex(control.pointIndex);
    }
  }

  // Find which point to constrain drawing relative to - either the previous
  // point in most cases, or the next point when moving the first point.
  constrainRelativePoint(points: Point[], relativePointIndex: number): Point {
    if (relativePointIndex === 0) {
      return points[relativePointIndex + 1];
    } else {
      return points[relativePointIndex - 1];
    }
  }

  // Handle mouse movements while dragging the control.
  anchoredActionHandler(
    eventData: MouseEvent,
    transform: Transform,
    x: number,
    y: number
  ): boolean {
    const connector = transform.target as Connector;
    const currentControl = connector.controls[
      connector.__corner as string
    ] as ConnectorPointControl;
    const pointer = { x, y };

    if (!currentControl) {
      return false;
    }

    if (!transform.constraining) {
      // If we are dragging an end of the connector, see if it is over an
      // object that can be attached to. If so, constrain the pointer to
      // one of the attachment points.
      if (currentControl.whichEnd !== null) {
        const canvas = transform.target.canvas;
        currentControl.overObject = currentControl._findShapeTargets(
          canvas,
          pointer,
          connector,
          eventData.ctrlKey
        );
        if (currentControl.overObject) {
          currentControl._snapToAttachmentPoint(
            canvas,
            connector,
            pointer as Point
          );
        } else {
          currentControl.overAttachmentPoint = null;
        }
      } else {
        currentControl.overObject = null;
        currentControl.overAttachmentPoint = null;
      }

      currentControl._attachDetachDuringDrag(connector, currentControl);
    }

    // Position the connector point.
    const mouseLocalPosition = fabric.util.transformPoint(
      pointer as Point,
      fabric.util.invertTransform(connector.calcTransformMatrix())
    );
    const connectorBaseSize = connector._getNonTransformedDimensions();
    const size = connector._getTransformedDimensions(0, 0);
    let finalPointPosition = {
      x:
        (mouseLocalPosition.x * connectorBaseSize.x) / size.x +
        connector.pathOffset.x,
      y:
        (mouseLocalPosition.y * connectorBaseSize.y) / size.y +
        connector.pathOffset.y,
    };

    // If the user is holding down the shift key, then constrain movement
    // to a standard axis.
    if (eventData?.shiftKey && !currentControl.overAttachmentPoint) {
      finalPointPosition = constrainToAxis(
        finalPointPosition,
        currentControl.constrainRelativePoint(
          connector.points as Point[],
          currentControl.pointIndex
        )
      );
    }

    // Constrain orthogonal lines.
    if (connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL) {
      const nextPoint =
        currentControl.pointIndex === 0
          ? connector.points[1]
          : connector.points[connector.points.length - 2];
      const afterIndex =
        currentControl.pointIndex === 0 ? 0 : connector.points.length - 2;

      if (RouterOrthogonal.segmentHorizontal(connector, afterIndex)) {
        nextPoint.y = finalPointPosition.y;
      } else {
        nextPoint.x = finalPointPosition.x;
      }
    }

    const newPoints = [...connector.points];
    newPoints[currentControl.pointIndex] = finalPointPosition;
    connector.set('points', newPoints);
    connector._setPositionDimensions();

    return true;
  }

  // Keep the overall object in same position as the handles move.
  anchorWrapper(
    anchorIndex: number,
    fn: (
      eventData: MouseEvent,
      transform: Transform,
      x: number,
      y: number
    ) => boolean
  ) {
    return function (
      eventData: MouseEvent,
      transform: Transform,
      x: number,
      y: number
    ) {
      const fabricObject = transform.target as Connector;
      const absolutePoint = fabric.util.transformPoint(
        {
          x: fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
          y: fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y,
        } as Point,
        fabricObject.calcTransformMatrix(true)
      );
      const actionPerformed = fn(eventData, transform, x, y);
      const connectorBaseSize = fabricObject._getNonTransformedDimensions();
      const newX =
        (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) /
        connectorBaseSize.x;
      const newY =
        (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) /
        connectorBaseSize.y;

      // @ts-expect-error types expect center/left/right string, but function seems to handle absolute values too
      fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
      return actionPerformed;
    };
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: unknown,
    fabricObject: Connector
  ) {
    if (!fabricObject.beingControlled) {
      // Render point we are dragging.
      renderDragControl.call(this, ctx, left, top, styleOverride, fabricObject);
    }

    if (this.overObject) {
      // Render drop targets on the object we are over.
      fetchAttachmentPoints(this.overObject, {
        withStandoff: false,
      }).forEach(attachment => {
        const localAttachPoint = fabric.util.transformPoint(
          attachment.position,
          fabricObject.canvas.viewportTransform!
        );
        renderConnectControl.call(
          this.overObject,
          ctx,
          localAttachPoint.x,
          localAttachPoint.y,
          attachment.attachmentPoint === this.overAttachmentPoint
        );
      });
    }
  }

  showRightClickMenu(
    controller: WhiteboardController,
    fabricObject: Connector,
    x: number,
    y: number
  ) {
    if (this.whichEnd === null) {
      controller.actionShowConnectorPointMenu({
        x,
        y,
        object: fabricObject,
        pointIndex: this.pointIndex,
      });
    }
  }
}

export default ConnectorPointControl;
